import * as d3 from 'd3';
import { type CommitEvent, type Task, type Filter as NodeFilter, passes } from './Tasks';
import type { TaskMap } from './Tasks';
import { generateID } from './Domain';
import moment from 'moment';
import {default as forceLimit} from 'd3-force-limit';
//import forceParentXBilateral from './util/forceParentXBilateral';
import forceLinkPull from './util/forceLinkPull';
import forceRectCollide from './util/forceRectCollide';

type HotRegion = {
  name: string;
  priority: number;
  borderX0: number;
  borderX1: number;
  borderY0: number;
  borderY1: number;
  fillColor: string;
  hoverFillColor?: string;
  borderColor: string;
  borderLineX?: number;
  textX?: number;
  textFill?: string;
  date?: string;
  showCondition: (node: Node) => boolean;
  dropNodeBehavior: (node: Node) => void;
}

type Node = d3.SimulationNodeDatum & {
  id: string,
  group?: number,
  task: Task,
  isHovered: boolean,
  isDragged: boolean,
  isAnchored: boolean
}
type Link = { source: Node, target: Node, id: string };

export const COLORS = {
  node: {
    stroke: '#000',
    strokeWidth: 2,
    strokeSelected: '#333',
    fillComplete: '#9f9',
    fillBlocked: '#bbb',
    fillAvailable: '#fff',
    fillGhost: '#fff',
    opacityGhost: 0.8,
  },

  border: {
    complete: '#599959',
    blocked: '#666',
    overdue: '#fa8',
    ghost: '#000',

    radiusExternal: 3,
    radiusInternal: 15,

    ghostDashArray: '10 4',

    strokeWidth: 2
  },

  edge: {
    strokeWidth: 5,
    start: '#9f9',
    end: '#000',
    startGhost: '#9f9',
    endGhost: '#000',
    opacityGhost: 0.99,
  },

  region: {
    complete: '#daffda',
    available: '#fff',
    blocked: '#ccc',
    overdue: '#fa8',

    strokeWidth: 2,

    opacity: .3,
    hoverOpacity: .7
  },

  text: {
    fill: '#001',
    size: 16
  },

  calendar: {
    stroke: '#777',
  }
}

const SIM = {
  alphaTarget: 0,
  alphaDecay: 0.005,
  ambientWarm: 0.091,

  force : {
    fGRAVITY: .0089,
    fCHARGE: -0.000,
    fCHARGE_DRAG: -100.000,
    //fCHARGE: -0.9,
    fLINK: .0001,
    fLINK_X: .1400,
    fLINK_X_PARENT: .0100,
    fLINK_Y: .0025,
    fCENTER: 0.0000,
    fWALL: 0.0001,
    //fFENCE: -9.999,
    //FENCE_DECAY: 3,
    fDATE: .001,
    CUSHION_WIDTH: 100,
    CUSHION_STRENGTH: 5,
    REHEAT_DURATION_MS: 3000
  },

  height: 800,

  completedTaskSetpoint: 0.150,
  blockedTaskSetpoint: 0.060

}

const LAYOUT = {
  labelOffsetX: 10,
  labelOffsetY: 0,
  taskHeight: 40,
  taskWidth: 150,
  gap: 10,
  parentOffsetX: -90,
  calendar: {
    days: 8,
    overdueHeight: 300,
    scaleTop: 300,
    scaleBottom: 800
  }
}

const linkGradient = [{offset: "10%", color: COLORS.edge.start}, {offset: "90%", color: COLORS.edge.end}];

// true: dragging snaps the node's origin directly to the raw mouse position.
// false: dragging preserves the offset between the node's origin and the
// point you grabbed it at (d3-drag's default subject-relative behavior).
const DRAG_TRACKS_MOUSE = true;


/** Constrain a number between min and max */
export function clamp (n : number, min : number, max : number) : number {
  return Math.min(Math.max(n,min), max)
}

/** Get the hex color of a node based on its properties */
export function nodeColor( node : Node ) : string {
  //if (node.task.isExternal) { return '#39f'}
  //if (node.task.status == 'complete') { return COLORS.node.fillComplete }
  if (node.task.isComplete) { return COLORS.node.fillComplete }
  if (node.task.isBlocked) { return COLORS.node.fillBlocked }
  return COLORS.node.fillAvailable;
}

/** Get the radius of a node based on its properties */
export function nodeSize(node: Node | null) : number {
  let size = 30;
  if (!node) {
    size = 30;
  } else {
    switch (node.task.priority) {
      case 1: size= 45; break;
      case 2: size= 35; break;
      case 3: size= 25; break;
      case 4: size= 20; break;
      case 5: size= 20; break;
      default: size= 10; break;
    }
  }

  return size * 0.8;
}

function isInRegion(region, event) {
  return event.x >= region.borderX0 && event.x <= region.borderX1
      && event.y >= region.borderY0 && event.y <= region.borderY1;
}

export type Config = {
  labelFilter: NodeFilter,
  taskFilter?: NodeFilter,
  dateLabelFormat: 'OFFSET' & string,
  futureDays: number
}


type selection = d3.Selection<SVGElement, unknown, null, unknown>;

export class Simulation {

  public sim : d3.Simulation<Node, Link>;
  private data : {nodes: Map<string, Node>, links: Link[]};
  private reheatTimer: ReturnType<typeof setTimeout>;

  svgRef : d3.Selection<SVGSVGElement, unknown, null, undefined>;
  containerRef : d3.Selection<HTMLElement, unknown, null, undefined>;


  gRegions : selection;
  gBorders : selection; 
  gGhostLink : selection; 
  gNode : selection; 
  gGhostNode : selection; 
  defs : selection;
  gLink : selection;
  gHotRegions: selection;
  gCalendar: selection;

  forceCharge: d3.ForceManyBody<Node>;

  calendarDays: number;
  calendarScale;// : d3.Scale

  regions : HotRegion[];
  calendarRegions : HotRegion[];
  config: Config;

  //nodeRef;

  width; height;

  selectTaskCallback : (id: string) => void;
  setAddDependencyTask : (id: string) => void;
  onCommitCallback : (c: CommitEvent[]) => void;
  hoverTaskCallback : (id: string) => void;

  constructor(svgRef : SVGSVGElement, 
    containerRef : HTMLDivElement, 
    selectTask: (id: string) => void,
    onCommit?: (c: CommitEvent[]) => void,
    setAddDependencyTask?: (id: string) => void,
    hoverTask?: (id: string) => void,
    config?: Config)
  {
    this.svgRef = d3.select(svgRef);
    this.containerRef = d3.select(containerRef);

    this.selectTaskCallback = selectTask;
    this.onCommitCallback = onCommit;
    this.setAddDependencyTask = setAddDependencyTask;
    this.hoverTaskCallback = hoverTask;

    this.calendarScale = d3.scaleLinear( [0,LAYOUT.calendar.days],
      [LAYOUT.calendar.scaleTop,LAYOUT.calendar.scaleBottom])

    this.config = config ?? Simulation.defaultConfig;

    window['sim'] = this;

  }

  static defaultConfig: Config = {
    labelFilter: {blocked: true, complete: false, external: true, resolved: true},
    taskFilter: {rejectAll: false, blocked: true, complete: true, external: true, resolved: true},
    dateLabelFormat: 'OFFSET',
    futureDays: 7
  }

  private scaleDate(node, offset=0) {
    //return this.calendarScale(3);
    const now = moment().startOf('day')

    if (!node.task.dueDate) {
      return null
      return this.calendarScale(0 + offset)
    }

    const then = moment(node.task.dueDate).startOf('day')
    const delta = d3.max([-now.diff(then, 'days'), -1])
    return this.calendarScale(delta + offset)

  }

  private scaleDateRaw(date, offset=0) {
    const now = moment().startOf('day')
    const then = moment(date).startOf('day')
    const delta = d3.max([-now.diff(then, 'days'), -1])
    return this.calendarScale(delta + offset)
  }

  private initSim() {
    this.sim = d3.forceSimulation();

    this.sim
      .alphaTarget(SIM.alphaTarget)
      .alphaDecay(SIM.alphaDecay)
      .force("charge", d3.forceManyBody()
        .strength((d: Node) => d.isDragged ? SIM.force.fCHARGE_DRAG : SIM.force.fCHARGE))
      //.force('gravity', d3.forceY(0).strength(SIM.force.fGRAVITY))
      .force('bounds', forceLimit().y0(LAYOUT.taskHeight).y1(this.height - LAYOUT.taskHeight)
        .cushionWidth(20)
        .cushionStrength(1)
      )
    
    this.forceCharge = this.sim.force("charge")

  }

  // Wraps an x0/x1/y0/y1 accessor so it reports "no limit" for a node that's
  // currently pinned by drag (fx/fy set). Without this, forceLimit's hard
  // "already out of bounds" branch synchronously overwrites node.x/y mid-tick
  // (see d3-force-limit's `node[coord] = range[...]`), and any other force
  // reading that node's position later in the same tick (e.g. forceParentX)
  // sees the clamped value instead of the real, drag-owned position.
  private freeWhileDragged(accessor: (d: any) => number, unboundedValue: number) {
    return (d: any) => d.fx != null || d.fy != null ? unboundedValue : accessor(d);
  }

  private limitForce() {
    const force = forceLimit()
      .cushionStrength(SIM.force.CUSHION_STRENGTH)
      .cushionWidth(SIM.force.CUSHION_WIDTH)
      //.radius(d => nodeSize(d) / 2)

    const { x0, x1, y0, y1 } = force;
    force.x0 = (accessor: (d: any) => number) => x0(this.freeWhileDragged(accessor, -Infinity));
    force.x1 = (accessor: (d: any) => number) => x1(this.freeWhileDragged(accessor, Infinity));
    force.y0 = (accessor: (d: any) => number) => y0(this.freeWhileDragged(accessor, -Infinity));
    force.y1 = (accessor: (d: any) => number) => y1(this.freeWhileDragged(accessor, Infinity));

    return force;
  }

  private addForces() {
    const completedTaskSetpoint = SIM.completedTaskSetpoint * this.width;
    //const blockedTaskSetpoint = SIM.blockedTaskSetpoint * this.width;

    this.sim
      //.force('leftBound', this.limitForce().x0(-this.width/2))
      //.force('rightBound', this.limitForce().x1(this.width/2))
      // Tasks are drawn toward the task completion boundary. Completed tasks do not experience this force
      .force('gravity', d3.forceX(completedTaskSetpoint)
        .strength((d:Node) => d.task.isComplete ? 0 : SIM.force.fGRAVITY)
      )

      // Tasks can't fall past their due date into the future
      .force('date-limit', this.limitForce()
        .y1(d => d.task.isComplete ? Infinity : (this.scaleDate(d,1) ?? Infinity) - LAYOUT.taskHeight)
        .cushionWidth(0)
        .cushionStrength(1)
      )

      // Tasks are always drawn toward their due date
      .force('date', d3.forceY(d => this.scaleDate(d,1) - LAYOUT.taskHeight)
        .strength((d:Node) => d.task.dueDate ? SIM.force.fDATE : 0)
      )

      // Tasks are separated by the completion boundary
      .force('completion', this.limitForce()
        .cushionWidth(10)
        .x0(d => d.task.isComplete ? completedTaskSetpoint : -Infinity)
        .x1(d => d.task.isComplete ? Infinity              : completedTaskSetpoint-LAYOUT.taskWidth)
      )
        //.x1(d => d.task.isComplete ? Infinity              : completedTaskSetpoint))
      
      // Tasks cannot lie inside one another
      .force("collide", forceRectCollide()
        .width(d => this.getTaskWidth(d) + LAYOUT.gap)
        .height(LAYOUT.taskHeight + LAYOUT.gap)
        .strength((a, b) => (a.isDragged || b.isDragged) ? 0 : 1)
      )
      .force('bounds', forceLimit().y0(LAYOUT.taskHeight).y1(this.height - 2*LAYOUT.taskHeight)
        .cushionWidth(0)
        .cushionStrength(1)
      )

  }

  private addLinkForce(links, scalar = 1) {
    this.sim.force('link',
      d3.forceLink(links)
        .id((d: Node) => d.id)
        .strength(SIM.force.fLINK * scalar));
  }

  private getExitCoords = (d: any): [number, number] => {
    return [d.x + this.getTaskWidth(d), d.y + LAYOUT.taskHeight / 2]
  }

  private getTaskWidth = (d:any): number => {
    const targetRect = this.gNode.select(`#${CSS.escape(d.id)}`)
    const width = targetRect.empty() ? NaN : +targetRect.attr('width')

    return Number.isFinite(width) ? width : LAYOUT.taskWidth
  }

  // Renders a link as a smooth bezier between the source's left edge and
  // the target's right edge, instead of a straight line.
  private linkPath = d3.linkHorizontal()
    .source((d: any): [number, number] => [d.source.x, d.source.y + LAYOUT.taskHeight / 2])
    .target(d => this.getExitCoords(d.target));
  


  public init() {
    console.log('init')
    const rect = this.containerRef.node().getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height || SIM.height;

    this.svgRef
      .attr('height', this.height)
      .attr('width', '100%')
      .attr('viewBox', [-this.width/2, 0, this.width, this.height])
      .selectAll('*').remove();

    this.gRegions = this.svgRef.append('g').attr('id', 'regions')
    this.gBorders = this.svgRef.append('g').attr('id', 'borders')
    this.gCalendar = this.gBorders.append('g').attr('id', 'calendar')
    this.gHotRegions = this.svgRef.append('g').attr('id', 'regions-hot')
    this.gLink = this.svgRef.append('g').attr('id', 'link')
    this.gGhostLink = this.svgRef.append('g').attr('id', 'ghost-link')
    this.gNode = this.svgRef.append('g').attr('id', 'node')
    this.gGhostNode = this.svgRef.append('g').attr('id', 'ghost-node')
    this.defs = this.svgRef.append('defs')


    this.gLink.attr('stroke-width', COLORS.edge.strokeWidth);
    this.gNode.attr('stroke-width', COLORS.node.strokeWidth)
      .attr('stroke', COLORS.node.stroke);

    this.gGhostNode
      .attr('stroke-width', COLORS.node.strokeWidth)
      .attr('stroke', COLORS.node.stroke)
      .attr('fill', COLORS.node.fillGhost)
      .attr('opacity', COLORS.node.opacityGhost);

    this.gBorders.attr('stroke-width', 2);

    this.initSim();

    this.sim.on('tick', this.tickFn);
    //this.nodeRef = this.gNode.selectAll('g');


    const ro = new ResizeObserver(entries => {
      const dims = entries[0].contentRect;
      this.width = dims.width;
      this.height = dims.height || this.height;
      this.onResize()
    })

    ro.observe(this.containerRef.node())
  }

  private drawCalendar() {
    let N_DAYS = this.config.futureDays;
    this.calendarScale = d3.scaleLinear( [0, this.config.futureDays],
      [LAYOUT.calendar.scaleTop,LAYOUT.calendar.scaleBottom])

    this.calendarRegions = d3.range(0, N_DAYS).map(d => { return {
      name: `T${d > 0 ? '+' : ''}${d} days`,
      priority: 2,
      borderX0: SIM.blockedTaskSetpoint * this.width,
      borderX1: SIM.completedTaskSetpoint * this.width,
      borderY0: this.calendarScale(d),
      borderY1: this.calendarScale(d+1),

      fillColor: '#fff',
      hoverFillColor: '#ddd',
      borderColor: '#777',
      borderLineX: -1000,
      showCondition: node => !node.task.isComplete,
      dropNodeBehavior: node => this.onCommit([{id: node.id, type: 'update', field: 'dueDate', value: this.nToDateString(d)}]),
      date: this.nToDateString(d)
    }})

    this.gCalendar.selectAll('rect').data(this.calendarRegions).join('rect')
      .classed('interactive', true)
      .attr('x', d => d.borderX0)
      .attr('y', d => d.borderY0)
      .attr('height', d => d.borderY1 - d.borderY0)
      .attr('width', d => d.borderX1 - d.borderX0)
      .attr('fill', d => d.fillColor)
      .attr('stroke', d => d.borderColor)
      .attr('stroke-width', COLORS.region.strokeWidth)
      .attr('fill-opacity', COLORS.region.opacity)
    
    this.gCalendar.selectAll('text.interactive').data(this.calendarRegions).join('text')
      .classed('interactive', true)
      .attr('x', d => (d.borderX0 + d.borderX1) / 2)
      .attr('y', d => (d.borderY0 + d.borderY1) / 2)
      .text(d => this.config.dateLabelFormat == 'OFFSET' ? d.name : d.date)
      .attr('text-anchor', 'middle')
      .attr('alignment-baseline', 'middle')
  }

  private nToDateString(n) {
    const now = moment()
    const then = now.add(n, 'days')
    return then.format('yyyy-MM-DD')
  }

  private onResize = () => {
    this.svgRef
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('viewBox', [-this.width/2, 0, this.width, this.height])

    this.gRegions.selectChildren('*').remove();

    this.addForces();
    this.drawCalendar();
    this.drawHotRegions();
  }

  private drawHotRegions() {
    const completedTaskSetpoint = SIM.completedTaskSetpoint * this.width;
    const blockedTaskSetpoint = SIM.blockedTaskSetpoint * this.width;

    const hotRegions: HotRegion[] = [{
      name: 'blocked',
      priority: 2,
      borderX0: -this.width / 2,
      borderX1: blockedTaskSetpoint,
      borderY0: 0,
      borderY1: this.height,
      fillColor: COLORS.region.blocked,
      borderColor: COLORS.border.blocked,
      borderLineX: blockedTaskSetpoint,
      textX: blockedTaskSetpoint - 10,
      textFill: COLORS.border.blocked,
      showCondition: () => false,
      dropNodeBehavior: (_) => {},
    }, {
      name: 'block',
      priority: 1,
      borderX0: -this.width / 2,
      borderX1: -this.width / 3,
      borderY0: 0,
      borderY1: this.height,
      fillColor: '#999',
      borderColor: '#999',
      borderLineX: -this.width/4,
      showCondition: () => true,
      dropNodeBehavior: (node) => this.setAddDependencyTask(node.id),
    }, {
      name: 'complete',
      priority: 2,
      borderX0: completedTaskSetpoint,
      borderX1: this.width / 2,
      borderY0: 0,
      borderY1: this.height,
      fillColor: COLORS.region.complete,
      borderColor: COLORS.border.complete,
      borderLineX: completedTaskSetpoint,
      textX: completedTaskSetpoint + 20,
      textFill: COLORS.border.complete,
      showCondition: (node) => !node.task.isComplete,
      dropNodeBehavior: (node) => this.onCommit([{id: node.id, type: 'update', field: 'isComplete', value: true}]),
    }, {
      name: 'available',
      priority: 1,
      borderX0: blockedTaskSetpoint,
      borderX1: completedTaskSetpoint,
      borderY0: 0,
      borderY1: this.height,
      fillColor: COLORS.region.available,
      borderColor: '',
      showCondition: (node) => node.task.isComplete,
      dropNodeBehavior: (node) => this.onCommit([{id: node.id, type: 'update', field: 'isComplete', value: false}]),
    }, {
      name: 'overdue',
      priority: 10,
      borderX0: -this.width,
      borderX1: completedTaskSetpoint,
      borderY0: 0,
      borderY1: LAYOUT.calendar.overdueHeight,
      fillColor: COLORS.region.overdue,
      borderColor: COLORS.border.overdue,
      showCondition: () => false,
      dropNodeBehavior: this.setNodeOverdue,
    }]

    this.regions = hotRegions;

    this.gHotRegions.selectAll('rect')
      .data(hotRegions).join('rect')
        .attr('x', d => d.borderX0)
        .attr('y', d => d.borderY0)
        .attr('height', d => d.borderY1 - d.borderY0)
        .attr('width', d => d.borderX1 - d.borderX0)
        .attr('fill', d => d.fillColor)
        .attr('stroke', d => d.borderColor)
        .attr('stroke-width', COLORS.region.strokeWidth)
        .attr('fill-opacity', COLORS.region.opacity)
        .classed('interactive', true)

    const textY = 300;
    this.gRegions.selectAll('text')
      .data(hotRegions.filter(d => d.textX != null)).join('text')
        .text(d => d.name)
        .attr('x', d => d.textX)
        .attr('transform', d => `rotate(-90,${d.textX},${textY})`)
        .attr('fill', d => d.textFill)
        .attr('y', textY)
        .attr('font-size', 20)
        .attr('font-weight', 300)
        .attr('text-anchor', 'end');

  }

  private setNodeOverdue(node: Node) {
    console.log("Set node ", node.id, " overdue (not implemented")
  }

  public freeze() {
    this.data.nodes.forEach(n => {
      n.fx = n.x;
      n.fy = n.y;
      n.vx = 0;
      n.vy = 0;
    })
    this.sim.alpha(0).alphaTarget(0);
  }

  public resume() {
    this.data.nodes.forEach(n => {
      n.fx = n.isAnchored ? n.fx : null;
      n.fy = n.isAnchored ? n.fy : null;
      n.vx = 0;
      n.vy = 0;
    })
    this.sim.alpha(SIM.ambientWarm).alphaTarget(SIM.alphaTarget).restart()
  }

  public isAnchored(id: string): boolean {
    return this.data?.nodes.get(id)?.isAnchored ?? false;
  }

  public setAnchored(id: string, value: boolean) {
    const node = this.data?.nodes.get(id);
    if (!node) return;

    node.isAnchored = value;
    node.fx = node.isAnchored ? node.x : null;
    node.fy = node.isAnchored ? node.y : null;

    this.sim.alpha(SIM.ambientWarm).restart();
  }

  // Temporarily disables collision so nodes can freely cross paths and
  // reorganize, then restores it after REHEAT_DURATION_MS.
  public reheat() {
    const collide = this.sim.force('collide') as any;
    collide?.strength(0);
    this.forceCharge.strength(-10);

    this.sim.alpha(1).alphaTarget(SIM.alphaTarget).restart();


    this.addLinkForce(this.visibleLinks(Array.from(this.data.nodes.values())), 500)

    clearTimeout(this.reheatTimer);
    this.reheatTimer = setTimeout(() => {
      collide?.strength(1);
      this.forceCharge.strength((d: Node) => d.isDragged ? SIM.force.fCHARGE_DRAG : SIM.force.fCHARGE)
      this.addLinkForce(this.visibleLinks(Array.from(this.data.nodes.values())))

    }, SIM.force.REHEAT_DURATION_MS);
  }

  private updateData(tasks: TaskMap) {
    this.data = this.data ?? { nodes: new Map<string, Node>(), links: []}

    const newNodes : Map<string,Node> = new Map();


    for (const [id, task] of Object.entries(tasks)) {
      const node = this.data.nodes.get(id) ?? {
        id: task.id,
        task: task,
        isHovered: false,
        isDragged: false,
        isAnchored: false,
        selected: false,
        x: Math.random()*200,
        y: this.scaleDateRaw(task.dueDate),
        vx: 0,
        vy: 0
      }

      node.task = task;

      newNodes.set(id, node)

    }

    this.data.links = Array.from(newNodes.values())
      .flatMap( (t:Node) => (t.task.dependsOn ?? [])
        .map((c:string) => ({source: newNodes.get(c), target: t, id: `${c}-${t.task.id}`}) ))
    
    
    this.data.nodes = newNodes;
  }

  // Nodes that fail taskFilter are excluded from the simulation entirely
  // (not just hidden), so they stop participating in collide/gravity/etc.
  // and get added back automatically once they pass the filter again.
  private visibleNodes(): Node[] {
    return Array.from(this.data.nodes.values())
      .filter(d => this.passes(d.task, this.config.taskFilter));
  }

  private linkEndpointId(end: string | Node): string {
    return typeof end === 'string' ? end : end.id;
  }

  private visibleLinks(nodes: Node[]): Link[] {
    const visibleIds = new Set(nodes.map(d => d.id));
    return this.data.links.filter(l =>
      visibleIds.has(this.linkEndpointId(l.source)) &&
      visibleIds.has(this.linkEndpointId(l.target)));
  }

  public passes = passes;

  /* Update the nodes to match the TaskMap provided **/
  public update(tasks: TaskMap) {
    this.updateData(tasks)
    this.sim.alpha(SIM.ambientWarm).restart();
    this.render();
  }
  
  private enterTasks(enter: d3.Selection<d3.EnterElement, Node, SVGElement, unknown>): d3.Selection<d3.BaseType, Node, SVGElement, unknown> {

    const obj = enter.append('g').attr('width', LAYOUT.taskWidth);
    obj.append('rect')
      .attr('width', d => this.passes(d.task, this.config.labelFilter) ? LAYOUT.taskWidth : LAYOUT.taskHeight)
      .attr('height', LAYOUT.taskHeight) 
      .attr('rx', d => d.task.isExternal ? COLORS.border.radiusExternal : COLORS.border.radiusInternal) 
      .attr('ry', d => d.task.isExternal ? COLORS.border.radiusExternal : COLORS.border.radiusInternal)
      .attr('fill', nodeColor)
      .attr('id', d => d.task.id)
      .attr('y', d => this.scaleDate(d, 1))

    obj.append('text').text(d => d.task.title)
      .attr('font-family', 'Helvetica')
      .attr('width', LAYOUT.taskWidth) 
      .attr('font-size', COLORS.text.size)
      .attr('stroke-weight', '0')
      .attr('stroke', 'none')
      .attr('fill', COLORS.text.fill)
      //.attr('transform', d => `rotate(30, ${d.x}, ${d.y})`)
      .attr('clip-path', d => `url(#clip-${d.task.id})`)
      .style('visibility', d => this.passes(d.task, this.config.labelFilter) ? undefined : 'hidden')
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'middle')

    //enter.each(d => d.fx = null)

    return obj


  }

  // Re-derives the visible node/link set from the current data + filters,
  // rebinds it to the simulation (so filtered-out tasks stop being
  // simulated and are added back once they pass again), and re-joins the DOM.
  private render() {
    const nodes = this.visibleNodes();
    const links = this.visibleLinks(nodes);

    console.debug("Updating sim with", nodes)

    this.sim.nodes(nodes)
    this.addLinkForce(links)

    // Both directions are bidirectional springs toward this offset, each
    // aggregated to the single most-restrictive relationship: a child tracks
    // its nearest parent, a parent tracks its most-demanding child. A plain
    // two-node link behaves like a normal spring either way; an interior
    // node in a chain stays anchored to its own parent (fLINK_X, stronger)
    // and only weakly relays a descendant's movement upstream (fLINK_X_PARENT).
    this.sim.force('parentX', forceLinkPull(links, 'x')
      .offset(d => (-this.getTaskWidth(d.target) + LAYOUT.parentOffsetX))
      .strength(SIM.force.fLINK_X)
      .sourceStrength(SIM.force.fLINK_X_PARENT)
    )

    // Tasks are vertically drawn toward their parent tasks. Parents do not
    // experience this force (unilateral, unlike parentX).
    this.sim.force('parentY', forceLinkPull(links, 'y').strength(SIM.force.fLINK_Y))

    const node = this.gNode.selectAll('g')
      .data(nodes, (d: Node) => d.task.id)
      .join(
        enter => { return this.enterTasks(enter) },
        update => {
          update.select('text').text(d => d.task.title)
            .style('visibility', d => this.passes(d.task, this.config.labelFilter) ? undefined : 'hidden')
          update.select('rect').transition().duration(200)
            .attr('width', d => this.passes(d.task, this.config.labelFilter) ? LAYOUT.taskWidth : LAYOUT.taskHeight)
            .attr('rx', d => d.task.isExternal ? COLORS.border.radiusExternal : COLORS.border.radiusInternal) 
            .attr('ry', d => d.task.isExternal ? COLORS.border.radiusExternal : COLORS.border.radiusInternal)
            .attr('fill', nodeColor);

          // Release nodes if they're no longer anchored
          this.sim.nodes().forEach(d => { if (!d.isAnchored) { d.fx = null; d.fy = null } })

          return update
        }
        ,
        exit => { exit.remove(); }
      );

    this.gLink.selectAll<SVGPathElement, Link>('path')
      .data(links, d => d.id)
      .join('path')
      .attr('fill', 'none')
      .attr('stroke', d=>`url(#grad-${d.id})`)
      .attr('stroke-width', d =>
        d.source.task.isComplete &&
        d.target.task.isBlocked ? 1 : COLORS.edge.strokeWidth)
      .attr('stroke-dasharray', d =>
        d.source.task.isComplete &&
        d.target.task.isBlocked ? '4' : '')

    const clipPaths = this.defs.selectAll('clipPath')
      .data(nodes, (d: Node) => d.task.id)
      .join('clipPath')
        .attr('id', d => `clip-${d.task.id}`);

    clipPaths.selectAll('rect')
      .data(d => [d])
      .join('rect')
        .attr('width', LAYOUT.taskWidth)
        .attr('height', LAYOUT.taskHeight);

    const gradients = this.defs.selectAll('linearGradient')
      .data(links, (d: Link) => d.id)
      .join('linearGradient')
        .attr('id', d => `grad-${d.id}`)
        .attr('gradientUnits', 'userSpaceOnUse')

    gradients
      .selectAll('stop')
      .data(linkGradient)
      .join('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color)


    node.on('click.select', (_e,d) => {this.selectTaskCallback(d.id)});
    node.on('mouseover.a', this.hoverNode);
    node.on('mousemove.a', this.attachTooltipToMouse)
    node.on('mouseout.a', () => { this.hoverNode(null,null) })

    // Add a drag behavior.
    this.applyDragListener();

    //viz_regions.on('click', e => selectNode());
    this.gRegions.on('click', _e => this._setSelectedTask(""));

  }


  private tickFn = () => {
    //const node = svg.select('g#node').selectAll('g');
    
    const node = this.gNode.selectAll('g');
    if (!node.size()) return

    //console.debug('tick', this.tickCount++);

    node.select('rect')
      .attr('x', (d: Node) => { return d.x })
      .attr('y', (d: Node) => { return d.y })

    node.select('text')
        .attr('x', (d:Node) => d.x + LAYOUT.labelOffsetX)
        .attr('y', (d:Node) => d.y + LAYOUT.taskHeight / 2 + LAYOUT.labelOffsetY)

    this.defs.selectAll<SVGRectElement, Node>('clipPath rect')
      .attr('x', d => d.x)
      .attr('y', d => d.y)

    this.gLink.selectAll<SVGPathElement, Link>('path')
      .attr('d', this.linkPath as any)

    this.defs.selectAll<SVGLineElement, Link>('linearGradient')
      .attr('x1', d => d.source.x)
      .attr('y1', d => d.source.y + LAYOUT.taskHeight / 2)
      .attr('x2', d => d.target.x + this.getTaskWidth(d.target))
      .attr('y2', d => d.target.y + LAYOUT.taskHeight / 2)

    //d3.select('#debug')
    d3.select('#debug').select('p.alpha').text(this.sim.alpha());
    
  }

  private currentClickIsDrag : boolean = false;

  private dragstarted = (event, d) => {
    console.debug("Drag began on node", d.id)
    const node = d3.select('g#node').selectAll<SVGGElement, Node>('g');

    if (!event.active) this.sim.alpha(SIM.ambientWarm).alphaTarget(SIM.ambientWarm).restart();
    event.subject.fx = event.subject.x;
    event.subject.fy = event.subject.y;
    node.on('mouseover.a', null)
    node.on('mousemove.a', null)
    node.on('mouseout.a', null)

    d3.select('#tooltip').classed('hidden',true);

    node.filter(n => n.id == event.subject.id).raise();
    this.currentClickIsDrag = false;

    d.isDragged = true;
    this.forceCharge.strength((d: Node) => d.isDragged ? SIM.force.fCHARGE_DRAG : SIM.force.fCHARGE)
  }


  private dragged = (event, d) => {
    const targetNode: Node = d;
    this.currentClickIsDrag = true;

    // event.x/event.y bake in the offset between the node's origin and
    // wherever it was grabbed (d3-drag's default subject-relative behavior).
    // d3.pointer gives the raw pointer position instead, so the origin
    // snaps directly under the mouse regardless of grab point.
    let [mouseX, mouseY] = [event.x, event.y]

    let pointer = d3.pointer(event.sourceEvent, this.gNode.node())
    let coords = DRAG_TRACKS_MOUSE ? {x: pointer[0], y:pointer[1]} : {x:event.x,y:event.y};

    event.subject.fx = clamp(mouseX, -this.width/2, this.width/2);
    event.subject.fy = clamp(mouseY, 0 - LAYOUT.taskHeight, this.height - LAYOUT.taskHeight);


    this.svgRef.selectAll<SVGRectElement, HotRegion>('rect.interactive')
      .attr('fill-opacity', region => {
        //if (!region.showCondition(targetNode)) return 0.1;
        const hovered = isInRegion(region, coords) && region.showCondition(targetNode);
        return hovered ? COLORS.region.hoverOpacity : COLORS.region.opacity;
      }).attr('fill', region => {
        const hovered = isInRegion(region, coords) && region.showCondition(targetNode);
        return hovered ? (region.hoverFillColor ?? region.fillColor) : region.fillColor;

      });
  }
  
  private dragended = (event, d) => {

    //const targetNode = event.subject;
    const targetNode = d;

    // Restore the target alpha so the simulation cools after dragging ends.
    if (!event.active) this.sim.alphaTarget(SIM.alphaTarget);

    // Unfix the subject position now that it’s no longer being dragged,
    // unless the node is anchored, in which case it stays pinned at fx/fy.
    if (!targetNode.isAnchored) {
      targetNode.fx = null;
      targetNode.fy = null;
    }

    let pointer = d3.pointer(event.sourceEvent, this.gNode.node())
    let coords = DRAG_TRACKS_MOUSE ? {x: pointer[0], y:pointer[1]} : {x:event.x,y:event.y};

    if (!this.currentClickIsDrag) {
      this.selectTaskCallback(d.id)
      return;
    }

    [...this.regions, ...this.calendarRegions].sort((a,b) => a.priority - b.priority).some(region => {
      if (isInRegion(region, coords) && region.showCondition(targetNode)) {
        console.log(`Node is in region ${region.name}`)
        region.dropNodeBehavior(targetNode);
        return true
      }
      return false
    });

    this.svgRef.selectAll<SVGRectElement, HotRegion>('rect.interactive')
      .attr('fill-opacity', COLORS.region.opacity)
      .attr('fill', region => region.fillColor)

    const node = this.gNode.selectAll('g')
    node.on('mouseover.a', this.hoverNode);
    node.on('mousemove.a', this.attachTooltipToMouse)
    node.on('mouseout.a', () => d3.select('#tooltip').classed('hidden',true))

    this.sim.nodes().map(d => d.isDragged = false)
    this.forceCharge.strength((d: Node) => d.isDragged ? SIM.force.fCHARGE_DRAG : SIM.force.fCHARGE)

  }

  private applyDragListener() {
    this.gNode.selectAll('g').call( 
      d3.drag().clickDistance(10)
      .on("start.d", this.dragstarted)
      .on("drag.d", this.dragged)
      .on("end.d", this.dragended)
    )
  }

  private attachTooltipToMouse = (event, _d) => {
    d3.select('#tooltip')
      .style('top', (event.y+10)+'px')
      .style('left', (event.x+10)+'px')
  }

  private hoverNode = (_event, d) => {
    if (!_event || !d) {
      d3.select("#tooltip").classed('hidden', true);
      return;
    }

    this.hoverTaskCallback(d.task.id);

    if (passes(d.task, this.config.labelFilter)) { return };

    d3.select("#tooltip").classed('hidden', false)
      .style('background-color', nodeColor(d));
  }

  public setLabelsVisible(visible: boolean) {
    this.gNode.selectAll('g text')
      .style('visibility', visible ? undefined : 'hidden');
  }

  public setConfig(vizConfig: Config) {
    console.log("Config update to", vizConfig)
    this.config = vizConfig;

    this.sim.alpha(SIM.ambientWarm).restart();
    this.render();
    this.drawCalendar();

    this.onResize();
  }

  private showSelectedTask(id) {
    // Reset charges
    this.sim.nodes().map(d => d.isDragged = false)
    this.forceCharge.strength((d: Node) => d.isDragged ? SIM.force.fCHARGE_DRAG : SIM.force.fCHARGE)

    this.gNode.selectAll('g')
      .attr('stroke', null)
      .classed('selected', false)
      .filter((d:Node) => d.id === id)
        .attr('stroke', COLORS.node.strokeSelected)
        .classed('selected', true)
        .raise();
    
    let taskNode = this.gNode.selectAll<SVGGElement, Node>('g')
        .filter((d:Node) => d.id === id)
        .node()
    if (taskNode) {
      taskNode.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
    
  }

  public setSelectedTask(id) {
    this.showSelectedTask(id)
  }

  private _setSelectedTask(id) {
    this.selectTaskCallback(id)
    //this.showSelectedTask(id)
  }

  onCommit(commit: CommitEvent[]) {

    this.onCommitCallback(commit);

  }

  //setAddDependencyTask(id) {}
  
  addDependency(childID) {
    this.showSelectedTask(childID);

    this.freeze();

    const node = this.gNode.selectAll('g')

    node.on(' .drag', null) // remove drag listeners

    node.filter((d:Node) => d.id != childID)
      .on('click.block', (_e: Event, d: Node) => {
        this.onCommit([{id: childID, type: 'block', blockerId: d.id}]) // add dependency
        this._setSelectedTask(d.id);
        this.addDependencyResume();
      })

    const childNode = this.data.nodes.get(childID)


    // Add ghost node
    const [exitX, exitY] = this.getExitCoords(childNode)
    const [ghostX, ghostY] = [exitX + 100, exitY]
    this.tempLine(exitX, exitY, ghostX, ghostY) // ghostLine
    const ghostNode = this.tempNode(ghostX, ghostY - LAYOUT.taskHeight / 2)

    ghostNode.on('click', () => {
      const newID = generateID();
      this.onCommit([
        { id: newID, type: 'add' }, 
        { id: childID, type: 'block', blockerId: newID }
      ]);
      this._setSelectedTask(newID);

      this.addDependencyResume()
    })

    this.gHotRegions.on('click.resume', () => {
      console.info('resuming')
      this.addDependencyResume()
    });

    console.debug('Sim entered add dependency mode')

        
  }

  private tempLine(x1, y1, x2, y2) : d3.Selection<SVGLineElement, unknown, null, unknown> {
    return this.gGhostLink.append('line')
      .attr('stroke', COLORS.edge.end)
      .attr('stroke-width', COLORS.edge.strokeWidth)
      .attr('x1', x1)
      .attr('y1', y1)
      .attr('x2', x2)
      .attr('y2', y2)
      .raise();
  }

  private tempNode(x,y) : d3.Selection<SVGRectElement, unknown, null, unknown> {
    //const r = nodeSize(null);
    //console.log('node selection:', node.size());

    const n = this.gGhostNode.append('rect')
      .attr('width', LAYOUT.taskWidth)
      .attr('height', LAYOUT.taskHeight) 
      .attr('rx', COLORS.border.radiusInternal) 
      .attr('ry', COLORS.border.radiusInternal)
      .attr('x',x)
      .attr('y',y)
      .attr('stroke', COLORS.border.ghost)
      .attr('stroke-dasharray', COLORS.border.ghostDashArray)
      .attr('opacity', 1)
      .attr('fill-opacity', 0)
      .raise();
    
    return n
  }

  addDependencyResume() {
    this.gGhostLink.selectAll('*').remove();
    this.gGhostNode.selectAll('*').remove();
    this.gNode.selectAll('g').on('click.block', null);
    this.gRegions.on('click.resume', null)

    this.resume()
    this.applyDragListener();
    this.setAddDependencyTask("");

    console.debug('Sim resumed')
  }

  // Renders a link as a bezier from the source's left edge to the target's
  // right edge. d3.linkHorizontal's control points sit at the signed
  // midpoint between the two ends, which collapses/reverses when the target
  // is to the left of the source (the common case for a dependency graph).
  // Using an always-positive, rightward bulge instead means every link
  // leaves its source heading right and loops back if it needs to.
  /*
  private linkPathCurvy = (d: any) => {
    const sx = d.source.x;
    const sy = d.source.y + LAYOUT.taskHeight / 2;
    const tx = d.target.x + LAYOUT.taskWidth;
    const ty = d.target.y + LAYOUT.taskHeight / 2;

    const bulge = -Math.max(Math.abs(tx - sx) / 2, LAYOUT.taskWidth / 2) * 1.0;

    return `M${sx},${sy} C${sx + bulge},${sy} ${tx - bulge},${ty} ${tx},${ty}`;
  };
  */
}

